/**
 * ConversationContextMenu — right-click context menu for conversation items
 * in the left panel. Built on @radix-ui/react-dropdown-menu's ContextMenu
 * pattern (using DropdownMenu with a right-click trigger).
 *
 * V1 actions: most are placeholders (console.log). "Share..." opens the
 * ShareModal, and "Copy link" copies a shareable URL to clipboard.
 */
import { useState, useCallback, type ReactElement, type ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Pencil,
  Pin,
  Share2,
  Link2,
  Archive,
  FileDown,
  Trash2,
  FolderInput,
} from 'lucide-react';
import { wayneFetch } from '@/api/wayneFetch';
import { useConversationsStore } from '@/stores/conversations.store';
import ShareModal from './ShareModal';

export interface ConversationContextMenuProps {
  readonly sessionId: string;
  readonly children: ReactNode;
}

export default function ConversationContextMenu({
  sessionId,
  children,
}: ConversationContextMenuProps): ReactElement {
  const [shareOpen, setShareOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const queryClient = useQueryClient();
  const { togglePin } = useConversationsStore();

  const archiveMutation = useMutation({
    mutationFn: (id: string) => wayneFetch(`/sessions/${id}/archive`, { method: 'POST' }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['sessions'] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => wayneFetch(`/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['sessions'] }); },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      wayneFetch(`/sessions/${id}`, { method: 'PUT', body: { title } }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['sessions'] }); },
  });

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setMenuOpen(true);
  }, []);

  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}/play?session=${sessionId}`;
    void navigator.clipboard.writeText(url);
  }, [sessionId]);

  return (
    <>
      {/* Wrapper that captures right-click */}
      <div onContextMenu={handleContextMenu} className="contents">
        {children}
      </div>

      <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenu.Trigger asChild>
          {/* Invisible anchor positioned at click coordinates */}
          <div
            className="fixed w-0 h-0 pointer-events-none"
            style={{ left: menuPosition.x, top: menuPosition.y }}
            aria-hidden="true"
          />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-50 min-w-[200px] rounded-lg border border-wayne-hairline bg-wayne-surface shadow-lg p-1"
            style={{ fontSize: 13 }}
            sideOffset={4}
            align="start"
          >
            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                const title = window.prompt('Rename conversation:');
                if (title?.trim()) {
                  renameMutation.mutate({ id: sessionId, title: title.trim() });
                }
              }}
            >
              <Pencil size={14} className="text-wayne-ink-3" />
              Rename...
            </DropdownMenu.Item>

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                togglePin(sessionId);
              }}
            >
              <Pin size={14} className="text-wayne-ink-3" />
              Pin to top
            </DropdownMenu.Item>

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                const spaceName = window.prompt('Move to space (enter space name):');
                if (spaceName?.trim()) {
                  void wayneFetch<{ spaceId: string }>('/sessions/spaces', {
                    method: 'POST',
                    body: JSON.stringify({ name: spaceName.trim() }),
                  }).then((space) => {
                    void wayneFetch(`/sessions/${sessionId}`, {
                      method: 'PUT',
                      body: JSON.stringify({ spaceId: space.spaceId }),
                    }).then(() => {
                      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
                      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
                    });
                  });
                }
              }}
            >
              <FolderInput size={14} className="text-wayne-ink-3" />
              Move to space...
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="h-px my-1 bg-wayne-hairline" />

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => setShareOpen(true)}
            >
              <Share2 size={14} className="text-wayne-ink-3" />
              Share...
            </DropdownMenu.Item>

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={handleCopyLink}
            >
              <Link2 size={14} className="text-wayne-ink-3" />
              Copy link
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="h-px my-1 bg-wayne-hairline" />

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                archiveMutation.mutate(sessionId);
              }}
            >
              <Archive size={14} className="text-wayne-ink-3" />
              Archive
            </DropdownMenu.Item>

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-ink hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                 
                console.log('[ConversationContextMenu] Export as Markdown', sessionId);
              }}
            >
              <FileDown size={14} className="text-wayne-ink-3" />
              Export as Markdown
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="h-px my-1 bg-wayne-hairline" />

            <DropdownMenu.Item
              className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-wayne-red hover:bg-wayne-surface-2 outline-none"
              onSelect={() => {
                deleteMutation.mutate(sessionId);
              }}
            >
              <Trash2 size={14} />
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} sessionId={sessionId} />
    </>
  );
}
